import {
  Box,
  Grid,
  makeStyles,
  TextField,
  Typography
} from "@material-ui/core";
import Form from "@saleor/components/Form";
import { onTimeIsTrue } from "@saleor/motherOrders/utils/handlers";
import { SmopOrderStatusEnum } from "@saleor/types/globalTypes";
import Moment from "moment";
import * as React from "react";

import { getDate, getTime } from "../handlers";
import { TransactionTimeInitialFormData } from "../types";
import ErrorText from "./ErrorText";

interface TransactionSectionProps {
  initialForm: TransactionTimeInitialFormData;
  setTransactionTime: (data: any) => void;
  loading: boolean;
  status: SmopOrderStatusEnum;
  error: {
    dateErrorMessage: null;
    timeErrorMessage: null;
    timeCreatedErrorMessage: null;
  };
  serverError: {
    timeDispatched: string | null;
    timeDelivered: string | null;
  };
  dateFieldsEditable?: boolean;
  isDisabledPandago?: boolean;
}

const useStyles = makeStyles(
  () => ({
    content: {
      marginBottom: "17px"
    },
    riderDetails: {
      alignItems: "left",
      display: "flex",
      flexDirection: "column",
      justifyContent: "left"
    },
    riderDetailsContent: {
      alignItems: "center",
      display: "flex",
      flexWrap: "wrap",
      fontSize: "17px",
      marginBottom: "6px"
    },
    sectionLabel: {
      margin: "0px",
      marginBottom: "6px",
      marginLeft: "5px"
    },
    subLabel: {
      fontWeight: 600
    }
  }),
  { name: "TransactionSection" }
);

const TransactionSection: React.FC<TransactionSectionProps> = props => {
  const {
    initialForm,
    setTransactionTime,
    loading,
    status,
    error,
    serverError,
    dateFieldsEditable,
    isDisabledPandago
  } = props;
  const classes = useStyles(props);

  return (
    <Form initial={initialForm} confirmLeave>
      {({ data: formData, change }) => {
        setTransactionTime(formData);
        return (
          <div>
            <h3 className={classes.sectionLabel}>
              {" "}
              <span> Transaction Time</span>
            </h3>
            <div className={classes.content}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={6}>
                  {dateFieldsEditable ? (
                    <TextField
                      id="txtAssignRiderDialogDateDispatched"
                      label={"Date Dispatched"}
                      type="date"
                      variant="outlined"
                      name="dateDispatched"
                      value={getDate(formData.dateDispatched)}
                      onChange={change}
                      InputLabelProps={{
                        shrink: true
                      }}
                      fullWidth
                      disabled={
                        loading || status === SmopOrderStatusEnum.CANCELED
                      }
                      error={!!error.dateErrorMessage}
                    />
                  ) : (
                    <div
                      id="txtAsssignRiderDialogDateDispatched"
                      style={{ paddingLeft: "9px" }}
                    >
                      <span>Date Dispatched</span>
                      <Typography>
                        {Moment(formData.dateDispatched).format("MM/DD/YYYY")}
                      </Typography>
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    id="inptAssignRiderDialogTimeDispatched"
                    label={"Time Dispatched"}
                    type="time"
                    variant="outlined"
                    name="timeDispatched"
                    value={getTime(formData.timeDispatched)}
                    onChange={change}
                    InputLabelProps={{
                      shrink: true
                    }}
                    fullWidth
                    disabled={
                      loading ||
                      status === SmopOrderStatusEnum.CANCELED ||
                      isDisabledPandago
                    }
                    error={
                      !!error.timeErrorMessage ||
                      !!error.timeCreatedErrorMessage
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  {dateFieldsEditable ? (
                    <TextField
                      id="txtAssignRiderDialogDateDelivered"
                      label={"Date Delivered"}
                      type="date"
                      variant="outlined"
                      name="dateDelivered"
                      value={getDate(formData.dateDelivered)}
                      onChange={change}
                      InputLabelProps={{
                        shrink: true
                      }}
                      fullWidth
                      disabled={
                        loading || status === SmopOrderStatusEnum.CANCELED
                      }
                      error={!!error.dateErrorMessage}
                    />
                  ) : (
                    <div
                      id="txtAssignRiderDialogDateDelivered"
                      style={{ paddingLeft: "9px" }}
                    >
                      <span>Date Delivered</span>
                      <Typography>
                        {Moment(formData.dateDelivered).format("MM/DD/YYYY")}
                      </Typography>
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    id="txtAssignRiderTimeDateDelivered"
                    label={"Time Delivered"}
                    type="time"
                    variant="outlined"
                    name="timeDelivered"
                    value={getTime(formData.timeDelivered)}
                    onChange={change}
                    InputLabelProps={{
                      shrink: true
                    }}
                    fullWidth
                    disabled={
                      loading ||
                      status === SmopOrderStatusEnum.CANCELED ||
                      isDisabledPandago
                    }
                    error={!!error.timeErrorMessage}
                  />
                </Grid>
                {(error.dateErrorMessage ||
                  error.timeErrorMessage ||
                  error.timeCreatedErrorMessage ||
                  serverError.timeDispatched ||
                  serverError.timeDelivered) && (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      textAlign: "center"
                    }}
                  >
                    <ErrorText error={error} serverError={serverError} />
                  </Grid>
                )}

                <Grid
                  id="txtAssignRiderDialogDwellTime"
                  item
                  xs={6}
                  sm={6}
                  md={6}
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <span className={classes.subLabel}>Dwell Time</span>
                  <span>{formData.dwellTime ? formData.dwellTime : "--"}</span>
                </Grid>
                <Grid
                  id="txtAssignRiderDialogTotalTime"
                  item
                  xs={6}
                  sm={6}
                  md={6}
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <span className={classes.subLabel}>Total Time</span>
                  <span>{formData.totalTime ? formData.totalTime : "--"}</span>
                </Grid>
                <Grid id="txtAssignRiderDialogOnTime" item xs={6} sm={6} md={6}>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingRight: "75px"
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {" "}
                      <span className={classes.subLabel}>Status</span>{" "}
                      <span>On Time</span>
                    </div>
                    <h3
                      style={{
                        color: formData.onTime
                          ? "#4ee64e"
                          : formData.onTime !== null &&
                            formData.onTime === false
                          ? "#ff7d7d"
                          : "#3D3D3D"
                      }}
                    >
                      {formData.onTime !== null
                        ? onTimeIsTrue(formData.onTime)
                        : "--"}
                    </h3>
                  </Box>
                </Grid>
              </Grid>
            </div>
          </div>
        );
      }}
    </Form>
  );
};

export default TransactionSection;
